import { Box, Button, Container, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { Formik } from 'formik'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useNavigate } from 'react-router-dom'
import { AppBar, FileInput, Icon, Loading } from '../../components'
import Storage, { USER_PROFILE } from '../../services/Storage'
import { CustomerProfile, CustomerCaseApi } from '../../services/Swagger/Services'
import { useAddBike } from './useAddBike'
import { useTranslation } from 'react-i18next'
import { number, object, string } from 'yup'

interface OutOfWarrantyModal {
  productModel: string
  repairShopId?: number
  subject: string
  description: string
  errorType: {
    errorTypeId?: number
    name: string
    active: boolean
  }
  files: Array<any>
  phoneNumber?: string
  address: string
}
function AddTicketWarranty() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const user: CustomerProfile = Storage.getItem(USER_PROFILE)
  const { isLoading, errorTypes, repairShops } = useAddBike()
  const [isSubmit, setIsSubmit] = useState(false)

  const [ticket, setTicket] = useState<OutOfWarrantyModal>({
    productModel: '',
    repairShopId: undefined,
    subject: '',
    description: '',
    errorType: {
      errorTypeId: undefined,
      name: '',
      active: true,
    },
    files: [],
    phoneNumber: user.phoneNumber ?? undefined,
    address: user?.address ?? '',
  })

  useEffect(() => {
    if (errorTypes.length > 0) {
      setTicket((prev) => ({
        ...prev,
        errorType: {
          ...prev.errorType,
          errorTypeId: errorTypes[errorTypes.length - 1].errorTypeId,
        },
      }))
    }
  }, [errorTypes])

  const onSubmit = async (values: OutOfWarrantyModal) => {
    setIsSubmit(true)

    try {
      await new CustomerCaseApi().apiCustomerCasesOutOfWarrantyPost(
        values.productModel,
        values.repairShopId,
        values.subject,
        values.description,
        values.errorType?.errorTypeId,
        values.phoneNumber,
        '',
        values.files,
        values.address,
      )
      navigate('/my-tickets')
    } catch (ex: any) {
      console.log(ex)
    } finally {
      setIsSubmit(false)
    }
  }

  const ticketSchema = object().shape({
    phoneNumber: number().required(),
    productModel: string().required(),
    subject: string().required(),
    description: string().required(),
    repairShopId: number().required().min(1, t('invalidRepairShop')),
    address: string().required(),
  })

  return (
    <Box>
      <Helmet>
        <title>Cykelmakker - Register a new bike</title>
      </Helmet>
      <AppBar />
      <Container maxWidth={'md'}>
        <Stack
          direction='row'
          spacing={2}
          sx={{
            justifyContent: 'space-between',
            my: 6,
          }}
        >
          <Box sx={{ width: 30 }}>
            <Link to='/'>
              <Icon name='icon-arrow-left' color='white' />
            </Link>
          </Box>
          <Box>
            <Typography variant='h3'>{t('createATicketWarranty')}</Typography>
          </Box>
          <Box sx={{ width: 30 }}></Box>
        </Stack>
        {isLoading && <Loading />}
        {!isLoading && (
          <Formik
            initialValues={ticket}
            enableReinitialize={true}
            validationSchema={ticketSchema}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, values, errors, isValid, setFieldValue }) => (
              <>
                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='caption' className='required'>
                    {t('yourBikeModel')}
                  </Typography>
                  <TextField
                    value={values.productModel}
                    autoComplete='off'
                    onChange={(e) => {
                      setFieldValue('productModel', e.target.value)
                    }}
                    fullWidth
                    variant='outlined'
                    placeholder={t('egSCOPremiumEMoon')}
                  />
                  {errors.productModel && (
                    <Typography className='textError'>{errors.productModel}</Typography>
                  )}
                </Box>
                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='caption' className='required'>
                    {t('yourProblem')}
                  </Typography>
                  <TextField
                    value={values.subject}
                    autoComplete='off'
                    onChange={(e) => {
                      setFieldValue('subject', e.target.value)
                    }}
                    fullWidth
                    variant='outlined'
                    placeholder={t('egDamagedBattery')}
                  />
                  {errors.subject && (
                    <Typography className='textError'>{errors.subject}</Typography>
                  )}
                </Box>
                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='caption' className='required'>
                    {t('errorType')}
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    value={values.errorType?.errorTypeId}
                    onChange={(value) => {
                      setFieldValue('errorType.errorTypeId', value.target.value)
                    }}
                    placeholder={t('errorType')}
                  >
                    {errorTypes?.map((pro) => (
                      <MenuItem key={pro.errorTypeId} value={pro.errorTypeId}>
                        {pro.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  {errors.errorType && (
                    <Typography className='textError'>{errors.errorType.errorTypeId}</Typography>
                  )}
                </Box>
                <Box sx={{ mb: 6, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='caption' className='required'>
                    {t('description')}
                  </Typography>
                  <TextField
                    value={values.description}
                    autoComplete='off'
                    onChange={(e) => {
                      setFieldValue('description', e.target.value)
                    }}
                    fullWidth
                    variant='outlined'
                    placeholder={t('pleaseDescribeYourProblemAsDetailedAsYouCan')}
                  />
                  {errors.description && (
                    <Typography className='textError'>{errors.description}</Typography>
                  )}
                </Box>
                <Box sx={{ mb: 2 }}>
                  <FileInput
                    label={t('bikePhotos')}
                    multiple={true}
                    accept={'image/*, video/*'}
                    onChange={async (files) => {
                      console.log(files)
                      if (files) {
                        setFieldValue('files', Array.from(files))
                      } else {
                        setFieldValue('files', [])
                      }
                    }}
                  />
                </Box>
                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='caption' className='required'>
                    {t('repairShop')}
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    value={values.repairShopId}
                    onChange={(value) => {
                      setFieldValue('repairShopId', value.target.value)
                    }}
                    placeholder={t('repairShop')}
                  >
                    {repairShops?.map((pro) => (
                      <MenuItem key={pro.repairshopId} value={pro.repairshopId}>
                        {pro.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  {errors.repairShopId && (
                    <Typography className='textError'>{errors.repairShopId}</Typography>
                  )}
                </Box>
                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='caption' className='required'>
                    {t('phoneNumber')}
                  </Typography>
                  <TextField
                    value={values.phoneNumber}
                    autoComplete='off'
                    type='tel'
                    onBeforeInput={(e) => {
                      const inputEvent = e as unknown as InputEvent
                      if (inputEvent.data && !/^\d*$/.test(inputEvent.data)) {
                        e.preventDefault()
                      }
                    }}
                    onChange={(e) => {
                      setFieldValue('phoneNumber', Number(e.target.value))
                    }}
                    fullWidth
                    variant='outlined'
                    placeholder={t('enterPhoneNumber')}
                  />
                  {errors.phoneNumber && (
                    <Typography className='textError'>{errors.phoneNumber}</Typography>
                  )}
                </Box>
                <Box sx={{ mb: 6, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='caption' className='required'>
                    {t('address')}
                  </Typography>
                  <TextField
                    value={values.address}
                    autoComplete='off'
                    onChange={(e) => {
                      setFieldValue('address', e.target.value)
                    }}
                    fullWidth
                    variant='outlined'
                    placeholder={t('addressPlaceholder')}
                  />
                  {errors.phoneNumber && (
                    <Typography className='textError'>{errors.phoneNumber}</Typography>
                  )}
                </Box>

                <Box sx={{ mb: 8 }}>
                  <Button
                    fullWidth
                    variant='contained'
                    disabled={isSubmit || !isValid}
                    onClick={() => handleSubmit()}
                  >
                    {t('save')}
                  </Button>
                </Box>
              </>
            )}
          </Formik>
        )}
      </Container>
    </Box>
  )
}

export default AddTicketWarranty
